import { ColumnDef } from '@tanstack/react-table';
import { ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from '../components/Header';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { DataTable } from '../components/ui/data-table';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { MessageDialog } from '../components/ui/message-dialog';
import { useDebouncedValue } from '../hooks/useDebouncedValue';

interface User {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  roles: Array<{ key: string; name: string; description: string }>;
}

interface AssetTemplate {
  id: string;
  name: string;
  description?: string;
  category?: {
    id: string;
    name: string;
  };
  images?: Array<{
    id: string;
    url: string;
    display_order: number;
  }>;
}

interface Asset {
  id: string;
  instance_number: string;
  serial_number?: string;
  current_status: string;
  template: {
    id: string;
    name: string;
    images?: Array<{
      id: string;
      url: string;
      display_order: number;
    }>;
  };
  qr_code_id?: string;
}

interface QRCodeData {
  id: string;
  code: string;
  status: string;
  asset_instance_id: string | null;
  asset_instance?: {
    id: string;
    instance_number: string;
    template: {
      name: string;
    };
  };
}

const SCAN_BASE_URL = process.env.REACT_APP_SCAN_BASE_URL || 'https://scan.homebound.co.uk';

export default function AssignQRCode() {
  const { id } = useParams<{ id?: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<User | null>(null);

  // Step 1: Code input
  const [codeInput, setCodeInput] = useState('');
  const [validatingCode, setValidatingCode] = useState(false);
  const [qrCode, setQrCode] = useState<QRCodeData | null>(null);
  const [codeError, setCodeError] = useState('');

  // Step 2: Product selection
  const [templates, setTemplates] = useState<AssetTemplate[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AssetTemplate | null>(null);
  const [templatesPagination, setTemplatesPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [templateSearch, setTemplateSearch] = useState('');
  const debouncedTemplateSearch = useDebouncedValue(templateSearch, 300);

  // Step 3: Asset instance selection
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loadingAssets, setLoadingAssets] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState<Asset | null>(null);
  const [assetsPagination, setAssetsPagination] = useState({ total: 0, page: 1, limit: 10, pages: 0 });
  const [assetSearch, setAssetSearch] = useState('');
  const debouncedAssetSearch = useDebouncedValue(assetSearch, 300);

  // Step 4: Confirmation
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  const currentStep = qrCode
    ? (selectedAsset ? 4 : (selectedTemplate ? 3 : 2))
    : 1;

  // Load user data
  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }
  }, []);

  // Load QR code if ID is in URL
  useEffect(() => {
    if (id) {
      loadQRCode(id);
    }
  }, [id]);

  const loadQRCode = async (codeId: string) => {
    try {
      setValidatingCode(true);
      const response = await fetch(`/api/qr-codes/${codeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        if (data.data.asset_instance_id) {
          setCodeError(`This QR code is already assigned to ${data.data.asset_instance?.template?.name || 'an asset'} (${data.data.asset_instance?.instance_number || 'Unknown'}). You can reassign it to a different asset.`);
        }
        setQrCode(data.data);
        setCodeInput(data.data.code);
        loadTemplates();
      } else {
        setCodeError('QR code not found');
      }
    } catch (error) {
      console.error('Error loading QR code:', error);
      setCodeError('Failed to load QR code');
    } finally {
      setValidatingCode(false);
    }
  };

  const handleValidateCode = async () => {
    try {
      setCodeError('');
      setValidatingCode(true);

      const response = await fetch(`/api/qr-codes/check/${codeInput}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();

      if (!data.success) {
        setCodeError('QR code not found');
        return;
      }

      // Check response now includes UUID - use it to load full details
      const qrCodeId = data.data.id;
      const qrResponse = await fetch(`/api/qr-codes/${qrCodeId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const qrData = await qrResponse.json();
      if (qrData.success && qrData.data) {
        const qrCodeData = qrData.data;
        if (qrCodeData.asset_instance_id) {
          setCodeError(`This QR code is already assigned to ${qrCodeData.asset_instance?.template?.name || 'an asset'} (${qrCodeData.asset_instance?.instance_number || 'Unknown'}). You can reassign it to a different asset.`);
        }
        setQrCode(qrCodeData);
        loadTemplates();
      }
    } catch (error) {
      console.error('Error validating code:', error);
      setCodeError('Failed to validate code');
    } finally {
      setValidatingCode(false);
    }
  };

  const loadTemplates = async (page = 1, limit = 10, search = '') => {
    try {
      setLoadingTemplates(true);
      // Only fetch products that have units available for QR assignment (no QR code assigned yet)
      const qs = new URLSearchParams({ page: String(page), limit: String(limit), has_available_for_qr: 'true' });
      if (search) qs.set('search', search);
      const response = await fetch(`/api/asset-templates?${qs.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        // Deduplicate by name (keep first occurrence) as a safeguard
        const uniqueTemplates = data.data.filter((template: AssetTemplate, index: number, self: AssetTemplate[]) =>
          index === self.findIndex((t) => t.name === template.name)
        );
        setTemplates(uniqueTemplates);
        if (data.pagination) {
          setTemplatesPagination({
            total: data.pagination.total,
            page: data.pagination.page,
            limit: data.pagination.limit,
            pages: data.pagination.pages || Math.ceil(data.pagination.total / data.pagination.limit),
          });
        }
      }
    } catch (error) {
      console.error('Error loading templates:', error);
    } finally {
      setLoadingTemplates(false);
    }
  };

  // Requery templates when search changes in Step 2
  useEffect(() => {
    if (qrCode && !selectedTemplate) {
      loadTemplates(1, templatesPagination.limit, debouncedTemplateSearch);
    }
  }, [debouncedTemplateSearch]);

  const loadAvailableAssets = async (templateId: string, page = 1, limit = 10, search = '') => {
    try {
      setLoadingAssets(true);
      const qs = new URLSearchParams({ template_id: templateId, page: String(page), limit: String(limit) });
      if (search) qs.set('search', search);
      const response = await fetch(`/api/asset-instances/for-qr-assignment?${qs.toString()}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
      });

      const data = await response.json();
      if (data.success) {
        setAssets(data.data);
        if (data.pagination) {
          setAssetsPagination({
            total: data.pagination.total,
            page: data.pagination.page,
            limit: data.pagination.limit,
            pages: data.pagination.totalPages || Math.ceil(data.pagination.total / data.pagination.limit),
          });
        }
      }
    } catch (error) {
      console.error('Error loading assets:', error);
    } finally {
      setLoadingAssets(false);
    }
  };

  // Requery assets when search changes in Step 3
  useEffect(() => {
    if (qrCode && selectedTemplate && !selectedAsset) {
      loadAvailableAssets(selectedTemplate.id, 1, assetsPagination.limit, debouncedAssetSearch);
    }
  }, [debouncedAssetSearch]);

  const handleConfirm = async () => {
    if (!qrCode || !selectedAsset) return;

    try {
      setAssigning(true);
      const response = await fetch(`/api/qr-codes/${qrCode.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ assetInstanceId: selectedAsset.id }),
      });

      const data = await response.json();

      if (data.success) {
        setConfirmDialogOpen(false);
        setSuccessDialogOpen(true);
        setTimeout(() => {
          navigate('/qr-codes');
        }, 1500);
      } else {
        setErrorMessage(data.message || 'Failed to assign QR code');
        setErrorDialogOpen(true);
      }
    } catch (error) {
      console.error('Error assigning code:', error);
      setErrorMessage('Failed to assign QR code');
      setErrorDialogOpen(true);
    } finally {
      setAssigning(false);
    }
  };

  const templateColumns: ColumnDef<AssetTemplate>[] = useMemo(() => [
    {
      accessorKey: 'name',
      id: 'product',
      header: 'Product',
      cell: ({ row }) => {
        const template = row.original;
        const imageUrl = template.images?.[0]?.url;

        return (
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 flex-shrink-0 bg-muted rounded overflow-hidden">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={template.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">
                  No image
                </div>
              )}
            </div>
            <div className="font-medium">{template.name}</div>
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          onClick={() => {
            setSelectedTemplate(row.original);
            loadAvailableAssets(row.original.id);
          }}
        >
          Select
        </Button>
      ),
    },
  ], []);

  const assetColumns: ColumnDef<Asset>[] = useMemo(() => [
    {
      accessorKey: 'instance_number',
      id: 'unit',
      header: 'Unit',
      cell: ({ row }) => {
        const asset = row.original;
        const imageUrl = asset.template?.images?.[0]?.url;

        return (
          <div className="flex items-center gap-3">
            <div className="h-16 w-16 flex-shrink-0 bg-muted rounded overflow-hidden">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={asset.template.name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="h-full w-full flex items-center justify-center text-muted-foreground text-xs">
                  No image
                </div>
              )}
            </div>
            <div>
              <div className="font-medium">{asset.template?.name || 'Unknown'}</div>
              <div className="text-sm text-muted-foreground">Instance: {asset.instance_number}</div>
            </div>
          </div>
        );
      },
      filterFn: (row, id, value) => {
        // Custom filter to search in instance_number, template name, and serial number
        const asset = row.original as Asset;
        const searchValue = String(value).toLowerCase();

        if (!searchValue) return true;

        return (
          asset.instance_number.toLowerCase().includes(searchValue) ||
          (asset.template?.name && asset.template.name.toLowerCase().includes(searchValue)) ||
          (asset.serial_number && asset.serial_number.toLowerCase().includes(searchValue)) ||
          false
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <Button
          size="sm"
          onClick={() => setSelectedAsset(row.original)}
          disabled={!!row.original.qr_code_id}
        >
          Select
        </Button>
      ),
    },
  ], []);

  return (
    <div className="min-h-screen bg-background">
      <Header user={user} currentPage="qr-codes" />

      <main className="container mx-auto px-4 py-8 max-w-4xl">
        <Button
          variant="ghost"
          onClick={() => navigate('/qr-codes')}
          className="mb-4"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to QR Codes
        </Button>

        <div className="mb-6">
          <h1 className="text-3xl font-bold">
            Assign QR Code
          </h1>
          <p className="text-muted-foreground mt-1">
            Step {currentStep} of 4: {
              currentStep === 1 ? 'Enter Code' :
              currentStep === 2 ? 'Select Product' :
              currentStep === 3 ? 'Select Unit' :
              'Confirm'
            }
          </p>
        </div>

      {/* Step 1: Code Input */}
      {!qrCode && (
        <Card>
          <CardHeader>
            <CardTitle>Step 1: Enter QR Code</CardTitle>
            <CardDescription>
              Scan or manually enter the QR code to assign
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="code">QR Code</Label>
              <div className="flex gap-2">
                <Input
                  id="code"
                  value={codeInput}
                  onChange={(e) => setCodeInput(e.target.value.toUpperCase())}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleValidateCode();
                    }
                  }}
                  placeholder="Enter or scan QR code"
                  autoFocus
                  disabled={validatingCode}
                />
                <Button onClick={handleValidateCode} disabled={!codeInput || validatingCode}>
                  {validatingCode && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Validate
                </Button>
              </div>
              {codeError && (
                <div className={`text-sm ${codeError.includes('reassign') ? 'text-yellow-600 dark:text-yellow-500' : 'text-destructive'}`}>
                  {codeError}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Product Selection */}
      {qrCode && !selectedTemplate && (
        <Card>
          <CardHeader>
            <CardTitle>Step 2: Select Product</CardTitle>
            <CardDescription>
              Choose which product type to assign QR code {qrCode.code} to
            </CardDescription>
          </CardHeader>
          <CardContent>
            <DataTable
              columns={templateColumns}
              data={templates}
              loading={loadingTemplates}
              searchKey="name"
              searchPlaceholder="Search products or categories..."
              searchValue={templateSearch}
              onSearchChange={(v) => { setTemplateSearch(v); }}
              emptyMessage="No products available for QR assignment. All units either have QR codes or no units exist."
              serverSide={true}
              pagination={templatesPagination}
              onPaginationChange={(newPage, newLimit) => loadTemplates(newPage, newLimit)}
            />
          </CardContent>
        </Card>
      )}

      {/* Step 3: Asset Instance Selection */}
      {qrCode && selectedTemplate && !selectedAsset && (
        <Card>
          <CardHeader>
            <CardTitle>Step 3: Select Unit</CardTitle>
            <CardDescription>
              Choose which {selectedTemplate.name} unit to assign QR code {qrCode.code} to
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setSelectedTemplate(null);
                  setAssets([]);
                }}
              >
                <ArrowLeft className="mr-2 h-4 w-4" />
                Back to Product Selection
              </Button>
            </div>
            <DataTable
              columns={assetColumns}
              data={assets}
              loading={loadingAssets}
              searchKey="instance_number"
              searchPlaceholder="Search units..."
              searchValue={assetSearch}
              onSearchChange={(v) => { setAssetSearch(v); }}
              emptyMessage="No available units found for this product"
              serverSide={true}
              pagination={assetsPagination}
              onPaginationChange={(newPage, newLimit) => loadAvailableAssets(selectedTemplate.id, newPage, newLimit, debouncedAssetSearch)}
            />
          </CardContent>
        </Card>
      )}

      {/* Step 4: Confirmation */}
      {qrCode && selectedAsset && (
        <Card>
          <CardHeader>
            <CardTitle>Step 4: Confirm Assignment</CardTitle>
            <CardDescription>
              Review and confirm the QR code assignment
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Product Image and Details Grid */}
            <div className="grid gap-6 md:grid-cols-2">
              {/* Left: Product Image */}
              <div className="flex flex-col">
                {(selectedTemplate?.images?.[0]?.url || selectedAsset.template?.images?.[0]?.url) ? (
                  <img
                    src={selectedTemplate?.images?.[0]?.url || selectedAsset.template?.images?.[0]?.url}
                    alt={selectedTemplate?.name || selectedAsset.template?.name || 'Product'}
                    className="w-full h-auto rounded-lg border-2 border-border object-cover"
                  />
                ) : (
                  <div className="w-full aspect-square rounded-lg border-2 border-border bg-muted flex items-center justify-center">
                    <div className="text-center text-muted-foreground">
                      <div className="text-4xl mb-2">📦</div>
                      <div className="text-sm">No image available</div>
                    </div>
                  </div>
                )}
                <div className="text-center mt-3 text-lg font-semibold">
                  {selectedTemplate?.name || selectedAsset.template?.name || 'Unknown Product'}
                </div>
              </div>

              {/* Right: Details */}
              <div className="space-y-6">
                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">QR Code</div>
                  <div className="text-lg font-mono font-bold">{qrCode.code}</div>
                  <div className="text-sm text-muted-foreground break-all">
                    {SCAN_BASE_URL}?q={qrCode.code}
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">Product</div>
                  <div className="text-lg font-semibold">{selectedTemplate?.name || 'Unknown Product'}</div>
                </div>

                <div className="space-y-2">
                  <div className="text-sm font-medium text-muted-foreground">Unit</div>
                  <div className="text-sm text-muted-foreground">
                    Instance: {selectedAsset.instance_number}
                    {selectedAsset.serial_number && ` • SN: ${selectedAsset.serial_number}`}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="secondary" onClick={() => setSelectedAsset(null)}>
                Back to Units
              </Button>
              <Button onClick={() => setConfirmDialogOpen(true)}>
                Confirm Assignment
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <ConfirmDialog
        open={confirmDialogOpen}
        onOpenChange={setConfirmDialogOpen}
        onConfirm={handleConfirm}
        title="Confirm Assignment"
        description={`Are you sure you want to assign QR code ${qrCode?.code} to ${selectedTemplate?.name || 'this product'} unit ${selectedAsset?.instance_number}?`}
        loading={assigning}
      />

      {/* Success Dialog */}
      {successDialogOpen && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="pt-6 text-center">
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Assignment Successful!</h3>
              <p className="text-muted-foreground">
                QR code has been assigned to the asset instance
              </p>
            </CardContent>
          </Card>
        </div>
      )}

      <MessageDialog
        open={errorDialogOpen}
        onOpenChange={setErrorDialogOpen}
        title="Error"
        description={errorMessage}
      />
      </main>
    </div>
  );
}

